import React, { useState, useEffect } from 'react';
import { analyticsAPI, salesAPI, saleReturnsAPI, exchangesAPI } from '../../api';
import dayjs from '../../utils/dayjs';
import { exportAnalyticsToPDF, exportAnalyticsAsImageToPDF } from '../../utils/analyticsPdf';
import { useAppStore } from '../../stores/useAppStore';
import { 
  TrendingUp, 
  TrendingDown, 
  ShoppingCart, 
  RotateCcw, 
  Package,
  Calendar,
  BarChart3,
  PieChart,
  RefreshCw,
  Download,
  FileText,
  Currency
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { printSalesExport, printReturnsExport, printExchangeReport } from '../../utils/pdf';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

const AnalyticsDashboard = () => {
  const { businessInfo } = useAppStore();
  const [todayStats, setTodayStats] = useState(null);
  const [popularItems, setPopularItems] = useState([]);
  const [salesTrend, setSalesTrend] = useState([]);
  const [revenueBreakdown, setRevenueBreakdown] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [reportDate, setReportDate] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [dailyBreakdown, setDailyBreakdown] = useState({ cash_received: 0, bank_received: 0, total_discount: 0, net_revenue: 0, exchange_cash_received: 0,  exchange_bank_received: 0, exchange_cash_returned: 0, sale_return_amount: 0, sale_return_quantity: 0});
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState('');

  // Reporting state
  const [reportStartDate, setReportStartDate] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [reportEndDate, setReportEndDate] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [salesReport, setSalesReport] = useState({ rows: [], totals: { sales_count: 0, cash_received: 0, bank_received: 0, cash_returned: 0, net_revenue: 0 } });
  const [returnItemsReport, setReturnItemsReport] = useState({ items: [], totals: { quantity: 0, amount: 0 } });
  const [exchangeItemsReport, setExchangeItemsReport] = useState({ returned: { items: [], totals: { quantity: 0, amount: 0 } }, given: { items: [], totals: { quantity: 0, amount: 0 } } });


  const setRestrictedReportStartDate = (newDate) => {
    const temp = dayjs(newDate).isAfter(dayjs(reportEndDate)) ? reportEndDate : newDate;
    if(temp) {
      setReportStartDate(temp);
    } else {
      setReportStartDate(newDate);
    }
  }

  const setRestrictedReportEndDate = (newDate) => {
    const temp = dayjs(newDate).isBefore(dayjs(reportStartDate)) ? reportStartDate : newDate;
    if(temp) {
      setReportEndDate(temp);
    } else {
      setReportEndDate(newDate);
    }
  }
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user's current date in their timezone
      const userToday = dayjs().format('YYYY-MM-DD');
      const userTimezone = dayjs.tz.guess(); // Get user's timezone

      const [todayStatsRes, popularItemsRes, salesTrendRes, revenueBreakdownRes, monthlyStatsRes] = await Promise.all([
        analyticsAPI.getTodayStats({ target_date: userToday, user_timezone: userTimezone }),
        analyticsAPI.getPopularItems({ limit: 10, days: 30, end_date: userToday, user_timezone: userTimezone }),
        analyticsAPI.getSalesTrend({ days: 7, end_date: userToday, user_timezone: userTimezone }),
        analyticsAPI.getRevenueBreakdown({ days: 30, end_date: userToday, user_timezone: userTimezone }),
        analyticsAPI.getMonthlyStats({ year: new Date().getFullYear() })
      ]);
      
      setTodayStats(todayStatsRes.data);
      setPopularItems(popularItemsRes.data);
      setSalesTrend(salesTrendRes.data);
      setRevenueBreakdown(revenueBreakdownRes.data);
      setMonthlyStats(monthlyStatsRes.data);
    } catch (err) {
      setError('Failed to load analytics data');
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Export: Returns only
  const handleExportReturnsReport = async () => {
    try {
      setReportLoading(true);
      setReportError('');
      const userTz = dayjs.tz.guess();
      
      // Use the dedicated analytics export endpoint
      const returnsRes = await analyticsAPI.exportReturns({ 
        start_date: reportStartDate, 
        end_date: reportEndDate, 
        user_timezone: userTz 
      });
      
      const returnsData = returnsRes.data;
      
      await printReturnsExport(returnsData);
      
    } catch (e) {
      console.error('Export returns error:', e);
      setReportError(e?.response?.data?.detail || 'Failed to export returns report');
    } finally {
      setReportLoading(false);
    }
  };


  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  // Daily report loader
  useEffect(() => {
    const load = async () => {
      try {
        setDailyLoading(true);
        setDailyError('');
        const userTimezone = dayjs.tz.guess();
        const startUtc = dayjs.tz(reportStartDate + 'T00:00:00', userTimezone).utc().format();
        const endUtc = dayjs.tz(reportEndDate + 'T23:59:59', userTimezone).utc().format();
        const res = await analyticsAPI.getDailySalesBreakdown({ report_start_date: startUtc, report_end_date: endUtc });
        setDailyBreakdown(res.data || { cash_received: 0, bank_received: 0, total_discount: 0, net_revenue: 0 , exchange_bank_received: 0, exchange_cash_received: 0, exchange_cash_returned: 0, sale_return_amount: 0, sale_return_quantity: 0});
      } catch (e) {
        setDailyError(e?.response?.data?.detail || 'Failed to load daily report');
        setDailyBreakdown({ cash_received: 0, bank_received: 0, total_discount: 0, net_revenue: 0, exchange_bank_received: 0, exchange_cash_received: 0, exchange_cash_returned: 0 , sale_return_amount: 0, sale_return_quantity: 0});
      } finally {
        setDailyLoading(false);
      }
    };
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [reportStartDate, reportEndDate]);


  // PDF Export Functions
  const handleExportToPDF = async () => {
    try {
      setExporting(true);
      
      const analyticsData = {
        todayStats,
        salesTrend,
        revenueBreakdown,
        popularItems,
        monthlyStats
      };
      
      await exportAnalyticsToPDF(analyticsData, null, businessInfo?.BusinessName);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Helper: inclusive date range check (user local date vs ISO datetime fields)
  const isInRange = (isoString) => {
    if (!isoString) return false;
    const start = dayjs.tz(reportStartDate + 'T00:00:00', dayjs.tz.guess()).utc();
    const end = dayjs.tz(reportEndDate + 'T23:59:59', dayjs.tz.guess()).utc();
    const d = dayjs(isoString);
    return d.isAfter(start.subtract(1, 'second')) && d.isBefore(end.add(1, 'second'));
  };

  // Generate and Export Reports
  const handleGenerateReports = async () => {
    try {
      setReportLoading(true);
      setReportError('');

      const userTz = dayjs.tz.guess();
      const startUtc = dayjs.tz(reportStartDate + 'T00:00:00', userTz).utc().format();
      const endUtc = dayjs.tz(reportEndDate + 'T23:59:59', userTz).utc().format();

      // 1) Sales report via server-side filtering
      const salesRes = await salesAPI.list({ start_date: startUtc, end_date: endUtc, limit: 1000 });
      const salesRows = Array.isArray(salesRes.data) ? salesRes.data : [];
      const salesTotals = salesRows.reduce((acc, row) => {
        const cash = Number(row.CashReceived || 0);
        const bank = Number(row.BankReceived || 0);
        const cashReturned = Number(row.CashReturned || 0);
        acc.sales_count += 1;
        acc.cash_received += cash;
        acc.bank_received += bank;
        acc.cash_returned += cashReturned;
        return acc;
      }, { sales_count: 0, cash_received: 0, bank_received: 0, cash_returned: 0, net_revenue: 0 });
      salesTotals.net_revenue = (salesTotals.cash_received + salesTotals.bank_received) - salesTotals.cash_returned;
      setSalesReport({ rows: salesRows, totals: salesTotals });

      // 2) Sale return items report (client-side filter + details per return)
      const returnsListRes = await saleReturnsAPI.list({ skip: 0, limit: 500 });
      const returnsInRange = (Array.isArray(returnsListRes.data) ? returnsListRes.data : []).filter(r => isInRange(r.SaleReturnDate));
      // fetch details per return id
      const returnDetailsArrays = await Promise.all(
        returnsInRange.map(r => saleReturnsAPI.listDetails(r.ID).then(res => res.data).catch(() => []))
      );
      // Flatten details and aggregate by item
      const returnItemsMap = {};
      let returnTotals = { quantity: 0, amount: 0 };
      returnDetailsArrays.flat().forEach(d => {
        const key = d.SaleItem_ID;
        const qty = Number(d.Quantity || 0);
        const price = Number(d.Price || 0);
        const discount = Number(d.Discount || 0);
        const amount = qty * (price - discount);
        if (!returnItemsMap[key]) returnItemsMap[key] = { SaleItem_ID: key, quantity: 0, amount: 0 };
        returnItemsMap[key].quantity += qty;
        returnItemsMap[key].amount += amount;
        returnTotals.quantity += qty;
        returnTotals.amount += amount;
      });
      const returnItemsData = { items: Object.values(returnItemsMap).sort((a,b) => b.amount - a.amount), totals: returnTotals };
      setReturnItemsReport(returnItemsData);

      // 3) Exchange items report (client-side filter + get exchange details per id)
      const exchangesListRes = await exchangesAPI.list({ skip: 0, limit: 500 });
      const exchangesInRange = (Array.isArray(exchangesListRes.data) ? exchangesListRes.data : []).filter(e => isInRange(e.ExchangeDate));
      const exchangeWithDetails = await Promise.all(
        exchangesInRange.map(e => exchangesAPI.get(e.ID).then(res => res.data).catch(() => null))
      );
      const returnedMap = {};
      const givenMap = {};
      const returnedTotals = { quantity: 0, amount: 0 };
      const givenTotals = { quantity: 0, amount: 0 };
      exchangeWithDetails.filter(Boolean).forEach(ex => {
        (ex.details || []).forEach(d => {
          const key = d.Item_ID;
          const qty = Number(d.Quantity || 0);
          const price = Number(d.Price || 0);
          const discount = Number(d.Discount || 0);
          const amount = qty * (price - discount);
          if (d.ExchangeType === 'RETURNED') {
            if (!returnedMap[key]) returnedMap[key] = { Item_ID: key, quantity: 0, amount: 0 };
            returnedMap[key].quantity += qty;
            returnedMap[key].amount += amount;
            returnedTotals.quantity += qty;
            returnedTotals.amount += amount;
          } else if (d.ExchangeType === 'GIVEN') {
            if (!givenMap[key]) givenMap[key] = { Item_ID: key, quantity: 0, amount: 0 };
            givenMap[key].quantity += qty;
            givenMap[key].amount += amount;
            givenTotals.quantity += qty;
            givenTotals.amount += amount;
          }
        });
      });
      const exchangeItemsData = {
        returned: { items: Object.values(returnedMap).sort((a,b) => b.amount - a.amount), totals: returnedTotals },
        given: { items: Object.values(givenMap).sort((a,b) => b.amount - a.amount), totals: givenTotals }
      };
      setExchangeItemsReport(exchangeItemsData);

      // Export to PDF
      const reportData = {
        dateRange: { start: reportStartDate, end: reportEndDate },
        sales: { rows: salesRows, totals: salesTotals },
        returns: returnItemsData,
        exchanges: exchangeItemsData
      };
      
      await exportAnalyticsToPDF(reportData, 'date-range-report', businessInfo?.BusinessName);

    } catch (e) {
      console.error('Report generation error:', e);
      setReportError(e?.response?.data?.detail || 'Failed to generate reports');
    } finally {
      setReportLoading(false);
    }
  };

  const handleExportAsImageToPDF = async () => {
    try {
      setExporting(true);
      await exportAnalyticsAsImageToPDF('analytics-dashboard');
    } catch (error) {
      console.error('Image PDF export error:', error);
      alert('Failed to export image PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return dayjs(dateString).format('MMM D');
  };

  // Chart data formatting functions for Chart.js
  const getSalesTrendChartData = (data) => {
    const formattedData = data.map(item => ({
      date: formatDate(item.date),
      sales: item.sales_count,
      revenue: parseFloat(item.revenue || 0)
    }));

    return {
      labels: formattedData.map(item => item.date),
      datasets: [
        {
          label: 'Sales Count',
          data: formattedData.map(item => item.sales),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          yAxisID: 'y',
        },
        {
          label: 'Revenue (PKR)',
          data: formattedData.map(item => item.revenue),
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          yAxisID: 'y1',
        },
      ],
    };
  };

  const getRevenueBreakdownChartData = (data) => {
    if (!data) return { labels: [], datasets: [] };
    
    return {
      labels: ['Cash Received', 'Bank Received', 'Discounts'],
      datasets: [
        {
          data: [data.cash_received, data.bank_received, data.total_discount],
          backgroundColor: ['#10B981', '#3B82F6', '#EF4444'],
          borderColor: ['#059669', '#2563EB', '#DC2626'],
          borderWidth: 2,
        },
      ],
    };
  };

  const getPopularItemsChartData = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        labels: ['No Data'],
        datasets: [
          {
            label: 'Quantity Sold',
            data: [0],
            backgroundColor: 'rgba(139, 92, 246, 0.6)',
            borderColor: '#8B5CF6',
            borderWidth: 1,
            maxBarThickness: 20,
          },
        ],
      };
    }
    
    const topItems = data.slice(0, 3); // Show only top 3 items
    
    return {
      labels: topItems.map(item => 
        item.item_name && item.item_name.length > 12 ? item.item_name.substring(0, 12) + '...' : (item.item_name || 'Unknown Item')
      ),
      datasets: [
        {
          label: 'Quantity Sold',
          data: topItems.map(item => Math.min(item.total_quantity_sold || 0, 100)), // Cap at 100 to prevent huge bars
          backgroundColor: 'rgba(139, 92, 246, 0.6)',
          borderColor: '#8B5CF6',
          borderWidth: 1,
          maxBarThickness: 20,
        },
      ],
    };
  };

  const getMonthlyChartData = (data) => {
    if (!data || !data.monthly_data) return { labels: [], datasets: [] };
    
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyData = Object.entries(data.monthly_data).map(([month, stats]) => ({
      month: months[parseInt(month) - 1],
      sales: stats.sales_count,
      revenue: parseFloat(stats.revenue || 0)
    }));

    return {
      labels: monthlyData.map(item => item.month),
      datasets: [
        {
          label: 'Sales Count',
          data: monthlyData.map(item => item.sales),
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: '#3B82F6',
          borderWidth: 1,
          yAxisID: 'y',
        },
        {
          label: 'Revenue (PKR)',
          data: monthlyData.map(item => item.revenue),
          backgroundColor: 'rgba(16, 185, 129, 0.8)',
          borderColor: '#10B981',
          borderWidth: 1,
          yAxisID: 'y1',
        },
      ],
    };
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (context.dataset.label === 'Revenue (PKR)') {
              return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
            }
            return `${context.dataset.label}: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${formatCurrency(context.parsed)}`;
          }
        }
      }
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="flex items-center gap-3">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-lg text-gray-600">Loading analytics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <TrendingDown className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Analytics</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchAnalyticsData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div id="analytics-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Overview of your business performance</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchAnalyticsData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Today's Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Today's Sales</p>
              <p className="text-2xl font-bold text-gray-900">{todayStats?.sales_count || 0}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-600">
              Revenue: <span className="font-medium text-green-600">{formatCurrency(todayStats?.sales_revenue || 0)}</span>
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Today's Returns</p>
              <p className="text-2xl font-bold text-gray-900">{todayStats?.returns_count || 0}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <RotateCcw className="h-6 w-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-600">
              Value: <span className="font-medium text-red-600">{formatCurrency(todayStats?.returns_value || 0)}</span>
            </p>
          </div>
        </div>

        {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Net Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(todayStats?.net_revenue || 0)}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
            <Currency className="h-6 w-6 text-blue-600" />
            
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-600">
              {todayStats?.net_revenue >= 0 ? (
                <span className="text-green-600 flex items-center gap-1">
                  <TrendingUp className="h-4 w-4" />
                  Positive
                </span>
              ) : (
                <span className="text-red-600 flex items-center gap-1">
                  <TrendingDown className="h-4 w-4" />
                  Negative
                </span>
              )}
            </p>
          </div>
        </div> */}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Popular Items</p>
              <p className="text-2xl font-bold text-gray-900">{popularItems.length}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Package className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-600">
              Top selling items (30 days)
            </p>
          </div>
        </div>
      </div>

      {/* Daily Sales Report (Cash/Bank/Discount/Net) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Sales Report</h3>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-gray-700">Start Date</label>
            <input
              type="date"
              value={reportStartDate}
              onChange={(e) => setRestrictedReportStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-gray-700">End Date</label>
            <input
              type="date"
              value={reportEndDate}
              onChange={(e) => setRestrictedReportEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        {dailyError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{dailyError}</div>
        )}
        {dailyLoading ? (
          <div className="flex items-center justify-center p-6">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="text-sm text-gray-500 mb-1">Cash Received</div>
              <div className="text-2xl font-semibold text-gray-900">{formatCurrency(dailyBreakdown.cash_received || 0)}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="text-sm text-gray-500 mb-1">Bank Received</div>
              <div className="text-2xl font-semibold text-gray-900">{formatCurrency(dailyBreakdown.bank_received || 0)}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="text-sm text-gray-500 mb-1">Cash Returned</div>
              <div className="text-2xl font-semibold text-gray-900">{formatCurrency(dailyBreakdown.cash_returned || 0)}</div>
            </div>
            {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="text-sm text-gray-500 mb-1">Net Revenue</div>
              <div className={`text-2xl font-semibold ${Number(dailyBreakdown.net_revenue || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {formatCurrency(dailyBreakdown.net_revenue || 0)}
              </div>
            </div> */}
          </div>
        )}
          <div className="flex items-center justify-between mb-4 mt-4">
            <h3 className="text-lg font-semibold text-gray-900">Daily Exchange Report</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="text-sm text-gray-500 mb-1">Cash Received</div>
              <div className="text-2xl font-semibold text-gray-900">{formatCurrency(dailyBreakdown.exchange_cash_received || 0)}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="text-sm text-gray-500 mb-1">Bank Received</div>
              <div className="text-2xl font-semibold text-gray-900">{formatCurrency(dailyBreakdown.exchange_bank_received || 0)}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="text-sm text-gray-500 mb-1">Cash Returned</div>
              <div className="text-2xl font-semibold text-gray-900">{formatCurrency(dailyBreakdown.exchange_cash_returned || 0)}</div>
            </div>
          </div>

            <div className="flex items-center justify-between mb-4 mt-4">
            <h3 className="text-lg font-semibold text-gray-900">Sale Return Report</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="text-sm text-gray-500 mb-1">Return Quantity</div>
              <div className="text-2xl font-semibold text-gray-900">{dailyBreakdown.sale_return_quantity || 0}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="text-sm text-gray-500 mb-1">Return Amount (In Cash)</div>
              <div className="text-2xl font-semibold text-gray-900">{formatCurrency(dailyBreakdown.sale_return_amount || 0)}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="text-sm text-gray-500 mb-1">Return Amount (From Bank)</div>
              <div className="text-2xl font-semibold text-gray-900">{formatCurrency(dailyBreakdown.sale_return_bank_amount || 0)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="text-sm text-gray-500 mb-1">Overall Cash</div>
                  <div className="text-2xl font-semibold text-gray-900">{formatCurrency((dailyBreakdown.cash_received + dailyBreakdown.exchange_cash_received + (-1 * dailyBreakdown.sale_return_amount) + (-1 * dailyBreakdown.exchange_cash_returned) ) + (-1 * dailyBreakdown.cash_returned) || 0)}</div>
            </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="text-sm text-gray-500 mb-1">Overall Bank</div>
                  <div className="text-2xl font-semibold text-gray-900">{formatCurrency((dailyBreakdown.exchange_bank_received + dailyBreakdown.bank_received) || 0)}</div>
            </div>
          </div>
      </div>

      {/* Reporting Section */}
      {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Reports</h3>
          
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 whitespace-nowrap">Start Date</label>
              <input
                type="date"
                value={reportStartDate}
                onChange={(e) => setReportStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 whitespace-nowrap">End Date</label>
              <input
                type="date"
                value={reportEndDate}
                onChange={(e) => setReportEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
        </div>

        
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <button 
            onClick={handleExportSalesReport} 
            disabled={reportLoading} 
            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <Download className="h-4 w-4" />
            {reportLoading ? 'Exporting...' : 'Export Sales (JSON)'}
          </button>
          <button 
            onClick={handleExportReturnsReport} 
            disabled={reportLoading} 
            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <Download className="h-4 w-4" />
            {reportLoading ? 'Exporting...' : 'Export Returns (JSON)'}
          </button>
          <button 
            onClick={handleExportExchangesReport} 
            disabled={reportLoading} 
            className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <Download className="h-4 w-4" />
            {reportLoading ? 'Exporting...' : 'Export Exchanges'}
          </button>
        </div>
        {reportError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{reportError}</div>
        )}

        {reportLoading ? (
          <div className="flex items-center justify-center p-6">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-4">
              <BarChart3 className="h-12 w-12 mx-auto mb-4" />
              <p>Select date range and click "Export Report" to generate PDF report</p>
            </div>
          </div>
        )}
      </div> */}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Trend Line Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Sales Trend (7 Days)</h3>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <div className="h-64">
            <Line 
              data={getSalesTrendChartData(salesTrend)} 
              options={chartOptions}
            />
          </div>
        </div>

        {/* Revenue Breakdown Doughnut Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Revenue Breakdown (30 Days)</h3>
            <PieChart className="h-5 w-5 text-gray-400" />
          </div>
          {revenueBreakdown && (
            <div className="h-64">
              <Doughnut 
                data={getRevenueBreakdownChartData(revenueBreakdown)} 
                options={doughnutOptions}
              />
            </div>
          )}
        </div>
      </div>

      {/* Additional Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Items Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 overflow-hidden">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Top Selling Items (30 Days)</h3>
            <Package className="h-5 w-5 text-gray-400" />
          </div>
          <div className="h-64 overflow-hidden relative" style={{ maxHeight: '256px' }}>
            {popularItems && popularItems.length > 0 ? (
              <div className="w-full h-full overflow-hidden">
                <Bar 
                  data={getPopularItemsChartData(popularItems)} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'y',
                    layout: {
                      padding: {
                        top: 10,
                        bottom: 10,
                        left: 10,
                        right: 10
                      }
                    },
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return `Quantity Sold: ${context.parsed.x}`;
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1
                        },
                        grid: {
                          display: false
                        }
                      },
                      y: {
                        ticks: {
                          maxRotation: 0,
                          minRotation: 0,
                          maxTicksLimit: 8
                        },
                        grid: {
                          display: false
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No sales data available for the selected period</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Monthly Performance Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Monthly Performance ({new Date().getFullYear()})</h3>
            <Calendar className="h-5 w-5 text-gray-400" />
          </div>
          <div className="h-64">
            <Bar 
              data={getMonthlyChartData(monthlyStats)} 
              options={chartOptions}
            />
          </div>
        </div>
      </div>

      {/* Detailed Popular Items Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Detailed Item Performance (30 Days)</h3>
          <Package className="h-5 w-5 text-gray-400" />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity Sold
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue Generated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sales Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg. Revenue/Sale
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {popularItems.map((item, index) => (
                <tr key={item.item_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                        index === 0 ? 'bg-yellow-100 text-yellow-800' :
                        index === 1 ? 'bg-gray-100 text-gray-800' :
                        index === 2 ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {index + 1}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 max-w-xs truncate" title={item.item_name}>
                      {item.item_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 font-medium">{item.total_quantity_sold}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 font-medium">{formatCurrency(item.total_revenue)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{item.sale_count}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatCurrency(item.sale_count > 0 ? item.total_revenue / item.sale_count : 0)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {popularItems.length === 0 && (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No sales data available for the selected period</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
